import { GetUserFavoritesDTO, SportType, UserAPI } from '@supersports/services';
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from './types';
import { userPolling } from './userSlice';

export type FavoriteSliceState = {
  list?: GetUserFavoritesDTO;
};

const initialState: FavoriteSliceState = {};

export const getUserFavorites = createAsyncThunk(
  'favorite/getUserFavorites',
  async (payload: { detail?: boolean; token?: string }, { getState }) => {
    const detail = (payload as { detail?: boolean })?.detail ?? false;
    const token = (payload as { token?: string })?.token || (getState() as RootState).user.SsToken;
    return await UserAPI.getFavorites(token, detail);
  }
);

/**
 * Updates the user's favorite list based on the provided MatchID and mode.
 *
 * @async
 * @param {object} options - The options object.
 * @param {number} options.MatchID - The ID of the match.
 * @param {string} options.mode - The mode to update the favorites list. Must be either 'add' or 'remove'.
 * @param {object} options.dispatch - The dispatch function of the Redux store.
 * @param {function} options.getState - The getState function of the Redux store.
 * @returns {void}
 *
 */
export const updateUserFavorites = createAsyncThunk(
  'favorite/updateUserFavorites',
  async (
    {
      MatchID,
      mode,
      sportType,
    }: {
      MatchID: number;
      mode: 'add' | 'remove';
      sportType: SportType;
    },
    { dispatch, getState }
  ) => {
    const state = getState() as RootState;
    const { SsToken } = state.user;
    const existingFavorites = (getState() as RootState).favorite.list?.Metadata?.[sportType] ?? [];
    const favoriteData =
      mode === 'add' ? [...existingFavorites, MatchID] : existingFavorites.filter((id) => id !== MatchID);
    await UserAPI.postFavorites(SsToken, favoriteData, sportType);
    await dispatch(getUserFavorites({ detail: true }));
  }
);

export const favoriteSlice = createSlice({
  name: 'favorite',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getUserFavorites.fulfilled, (state, action) => {
        if (!state.list) state.list = action.payload;
        else {
          state.list.Metadata = action.payload.Metadata;
          if ((action.meta.arg as any)?.detail) {
            state.list.Details = action.payload.Details;
          }
        }
      })
      .addCase(userPolling.fulfilled, (state, action) => {
        if (action.payload && state.list) {
          state.list.Metadata = action.payload.Favorite.Data.Metadata;
        }
      });
  },
});

export default favoriteSlice.reducer;

export const getTotalFavoritesSelector = createSelector(
  (state: RootState) => state.favorite.list,
  (list) => {
    if (!list) return 0;
    return Object.values(list.Metadata).reduce((acc, cur) => acc + cur.length, 0);
  }
);
